import type { ReactElement } from "react";
import { NavLink, Outlet } from "react-router";
import { Container, Stack, Text } from "../ui-kit";
import { getBuildInfo } from "../config";
import { SignOutButton } from "../auth/SignOutButton";
import { BrandMark } from "./BrandMark";
import { ThemeToggle } from "./ThemeToggle";
import styles from "./AppLayout.module.css";

interface NavItem {
  readonly to: string;
  readonly label: string;
}

/** The whole navigation model. Three read-only views; no write action exists. */
const NAV_ITEMS: readonly NavItem[] = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/users", label: "Users" },
  { to: "/documents", label: "Documents" },
];

const DEV_NAV_ITEM: NavItem = { to: "/ui-kit", label: "UI Kit" };

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return [styles.navLink, isActive ? styles.navLinkActive : undefined].filter(Boolean).join(" ");
}

/**
 * The application shell: a skip link, a fixed brand header, a persistent
 * navigation region, and the routed content area.
 *
 * The navigation is one `<nav>` in the DOM at every size — a column rail from
 * 768px up, a horizontal strip below it — so keyboard order and the
 * accessibility tree stay identical across breakpoints instead of forking into
 * a desktop menu and a mobile menu that drift apart.
 */
export function AppLayout(): ReactElement {
  const { buildVersion, deployedAt } = getBuildInfo();
  const items = import.meta.env.DEV ? [...NAV_ITEMS, DEV_NAV_ITEM] : NAV_ITEMS;

  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href="#main-content">
        Skip to main content
      </a>

      <header className={styles.header}>
        <Container width="wide" className={styles.headerInner}>
          <div className={styles.brand}>
            <BrandMark />
            <span className={styles.brandText}>
              <Text as="span" variant="subheading" className={styles.brandName}>
                AI Consultant
              </Text>
              <Text as="span" variant="eyebrow" tone="muted" className={styles.brandEyebrow}>
                Admin
              </Text>
            </span>
          </div>
          <Stack direction="row" gap={2} align="center" className={styles.headerActions}>
            <ThemeToggle />
            <SignOutButton />
          </Stack>
        </Container>
      </header>

      <Container width="wide" className={styles.body}>
        <nav className={styles.nav} aria-label="Sections">
          <ul className={styles.navList}>
            {items.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} className={navLinkClass} end>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Each view owns the single `<h1>` (see shell/PageHeader), so the
            heading order is view-title → section, never shell → view.
            `tabIndex={-1}` makes the skip link actually MOVE focus here: a
            fragment jump alone only shifts Chromium's sequential-focus
            starting point, so in other engines a screen-reader user activates
            the skip link and focus stays on `<body>`. */}
        <main id="main-content" tabIndex={-1} className={styles.main}>
          <Outlet />
        </main>
      </Container>

      <footer className={styles.footer}>
        <Container width="wide">
          <Text variant="caption" tone="muted">
            Read-only metadata. No document contents, file names, or classifier tags are shown
            here.
            {buildVersion !== undefined ? ` · Build ${buildVersion}` : ""}
            {deployedAt !== undefined ? ` · Deployed ${deployedAt}` : ""}
          </Text>
        </Container>
      </footer>
    </div>
  );
}
